import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_gsnetdef` raw rows into `im_inventory_policies`.
 *
 * Stage 2 of the GSNETDEF ingest pipeline. GSNETDEF carries GAINS-computed
 * MIN/MAX at SKU×branch grain. Upserts one policy row per (warehouse, product),
 * overwriting min/max each run (GAINS-authoritative). Rows whose warehouse or
 * product cannot be resolved are skipped + counted.
 *
 * The transform itself scans staged rows by id pagination (full replacement
 * semantics — GSNETDEF is dropped nightly), so `updatedAfter` is informational
 * only for now.
 */
export const transformGsnetdefPoliciesStep = defineStepConfig({
  type: "transform_gsnetdef_policies",
  name: "Transform GSNETDEF → Inventory Policies",
  description:
    "Reads rexel_gsnetdef rows and upserts im_inventory_policies with GAINS-computed min/max per (warehouse, product).",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — informational only; the transform reads the full staged set.",
      ),
  }),
  outputSchema: z.object({
    rowsScanned: z.number(),
    policiesUpserted: z.number(),
    skippedNoWarehouse: z.number(),
    skippedNoProduct: z.number(),
    skippedNoVariant: z.number(),
    updatedAfter: z.string().optional(),
  }),
  configSchema: z.object({
    divisionCode: z
      .string()
      .optional()
      .describe(
        "Optional 2-char AS400 division code (e.g. RA). Scopes the staged rows. " +
          "If omitted, every division's staged rows are transformed.",
      ),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
